import AddMultipleTracksModal from '@/components/common/MultipleTracks/AddMultipleTracksModal';
import { StateHistory } from '@/data/models/stateHistory';
import { ITrack } from '@/data/models/track';
import { IValidationResults } from '@/data/models/validation';
import { useProductTabs } from '@/hooks/useProductTabs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import CopyProductMetadataToTracksModal from '../CopyProductMetadataToTracksModal/CopyProductMetadataToTracksModal';
import MoveTracksToVolumeModal, {
  MoveTracksToNewVolumeConfirmationModal,
} from '../MoveTracksToVolumeModal';
import { RestrictionSet } from '../Restrictions/RestrictionSet';
import RestrictionsTab from '../Restrictions/RestrictionsTab';
import GroupedProducts from './GroupedProducts/GroupedProducts';
import './ProductTabs.scss';
import StateHistoryTab from './StateHistory/StateHistoryTab';
import PriceTab from './PriceTab';
import { IAlbum } from '@/data/models/album';
import TrackGrid from './TrackGrid';
import LookupTrackModal from '../LookupTrackModal';
import { ProductViewModes } from '@/utils/productStateUtils';
import ChangeRequestCounter from '@/components/common/ChangeRequestCounter/ChangeRequestCounter';
import { ICurrentChanges } from '@/types/product-current-changes-types';
import { IChangeRequest } from '@/data/models/changeRequest';
import {
  isValidationError,
  isValidationWarning,
} from '@/utils/validationErrors';

interface AugmentedTrack extends ITrack {
  metadataStatus: 'valid' | 'warning' | 'error' | 'loading';
}

const MetadataCellRenderer = (params: any) => {
  const { t } = useTranslation();
  const status = params.data.metadataStatus;

  if (status === 'loading') {
    return (
      <i
        className="icon fas fa-spinner fa-spin fa-lg"
        title={t('common.generic.loading')}
        aria-hidden="true"
      ></i>
    );
  }
  if (status === 'error') {
    return (
      <i
        className="icon fas fa-exclamation-triangle fa-lg danger"
        title={t('product.editProduct.metadataError')}
        aria-hidden="true"
      ></i>
    );
  }
  if (status === 'warning') {
    return (
      <i
        className="icon fas fa-check fa-lg success icon-stack"
        title={t('product.editProduct.metadataWarning')}
        aria-hidden="true"
      >
        <i
          className="icon warning icon-bottom-right fas fa-exclamation-circle"
          aria-hidden="true"
          style={{ zIndex: 1 }}
        ></i>
      </i>
    );
  }
  return (
    <i
      className="icon fas fa-check fa-lg success"
      title={t('product.editProduct.metadataValid')}
      aria-hidden="true"
    ></i>
  );
};

const TracksChangesCounterCellRenderer = (params: any) => {
  const currentTrack = params.data;
  const trackChanges = params.context.trackChangeRequestChanges.tracks;
  const trackChangesCounter = trackChanges.find(
    (t: any) => t.id === currentTrack.id
  )?.changesCounter;

  return (
    <ChangeRequestCounter counter={trackChangesCounter}></ChangeRequestCounter>
  );
};

interface Props {
  isValidFormat: boolean;
  usesSides: boolean;
  tracks: ITrack[];
  reloadTracks: () => Promise<void>;
  restrictions?: RestrictionSet;
  stateHistory?: StateHistory[];
  toggleTrackPopUp: any;
  toggleMultipleTracksPopUp: any;
  toggleRestrictionsModal: any;
  togglePricingModal?: any;
  album: IAlbum;
  readonly: boolean;
  errors: IValidationResults[];
  updateProductTracks: (tracks: ITrack[]) => Promise<void>;
  isValidating?: boolean;
  trackSelectionResetSignal?: number;
  productViewMode?: ProductViewModes;
  currentChanges?: ICurrentChanges;
  changeRequest?: IChangeRequest | undefined;
}

function ProductTabs({
  isValidFormat,
  usesSides,
  tracks,
  reloadTracks,
  restrictions,
  stateHistory,
  toggleTrackPopUp,
  toggleMultipleTracksPopUp,
  toggleRestrictionsModal,
  togglePricingModal,
  album,
  readonly,
  errors,
  updateProductTracks,
  isValidating,
  trackSelectionResetSignal,
  productViewMode,
  currentChanges,
  changeRequest,
}: Props) {
  const { t } = useTranslation();

  const augmentedTracks: AugmentedTrack[] = useMemo(() => {
    if (!tracks) return [];

    if (isValidating) {
      return tracks.map((track) => ({ ...track, metadataStatus: 'loading' }));
    }

    const trackErrors =
      errors?.filter(
        (e) => e.validationLevel === 'Track' && e.trackId != null
      ) ?? [];

    return tracks.map((track) => {
      const errorsForTrack = trackErrors.filter((e) => e.trackId === track.id);

      if (errorsForTrack.some(isValidationError)) {
        return { ...track, metadataStatus: 'error' };
      }
      if (errorsForTrack.some(isValidationWarning)) {
        return { ...track, metadataStatus: 'warning' };
      }
      return { ...track, metadataStatus: 'valid' };
    });
  }, [tracks, errors, isValidating]);

  const {
    tabIndex,
    setTabIndex,
    volumeNumbers,
    isValidRestrictions,
    showReorderTracks,
    setShowReorderTracks,
    showConfirmationModal,
    setShowConfirmationModal,
    confirmationModalBody,
    confirmationModalHeader,
    confirmationModalAction,
    groupedProducts,
    getLocaleText,
    addNewTrack,
    addMultipleTracks,
    handleAddMultipleTracks,
    showAddMultipleTracks,
    setShowAddMultipleTracks,
    AddVolume,
    addRemoveTrack,
    formatHeader,
    finalColumnDefs: originalColumnDefs,
    isSelectedAllChecked,
    isSelectedAllIntermediate,
    handleSelectAllTracks,
    deleteSelectedTracks,
    editSelectedTracks,
    onSelectionChanged,
    rowSelection,
    getRowId,
    gridsRef,
    selectedTracks,
    showMoveTracksToVolumeModal,
    setShowMoveTracksToVolumeModal,
    showCopyProductMetadataModal,
    setShowCopyProductMetadataModal,
    showLookupTrackModal,
    setShowLookupTrackModal,
    lookupSource,
    lookupTrack,
    handleAddToVolumeClick,
    handleCopyProductMetadataClick,
    showMoveTracksToNewVolumeConfirmationModal,
    setShowMoveTracksToNewVolumeConfirmationModal,
  } = useProductTabs({
    tracks: augmentedTracks,
    album,
    reloadTracks,
    errors,
    usesSides,
    toggleTrackPopUp,
    toggleMultipleTracksPopUp,
    trackSelectionResetSignal,
  });

  const finalColumnDefs = useMemo(() => {
    if (!originalColumnDefs) return [];

    const columnDefs = [...originalColumnDefs];

    if (
      productViewMode === ProductViewModes.ChangeRequest ||
      productViewMode === ProductViewModes.ChangeRequestReadOnly
    ) {
      const changesColumnDef = {
        headerName: 'Changes',
        headerNameTranslationKey: 'track.changes',
        field: 'changesCounter',
        cellRenderer: TracksChangesCounterCellRenderer,
        width: 100,
        cellClass: 'text-center',
      };
      columnDefs.splice(columnDefs.length - 1, 0, changesColumnDef);
    }

    const metadataCol = {
      headerName: 'Metadata',
      field: 'metadataStatus',
      cellRenderer: MetadataCellRenderer,
      width: 120,
      cellClass: 'text-center',
    };

    const actionColIndex = columnDefs.findIndex(
      (c: any) => c.field === 'duration'
    );
    if (actionColIndex > -1) {
      const newDefs = [...columnDefs];
      newDefs.splice(actionColIndex, 0, metadataCol);
      return newDefs;
    }

    return [...columnDefs, metadataCol];
  }, [originalColumnDefs, productViewMode]);

  const pricingIssues =
    errors?.filter((e) => e.fieldName === 'AlbumSalesTeams') ?? [];
  const hasPricingErrors = pricingIssues.some(isValidationError);
  const hasPricingWarnings =
    !hasPricingErrors && pricingIssues.some(isValidationWarning);
  const pricingTabClassName = hasPricingErrors
    ? 'has-error'
    : hasPricingWarnings
      ? 'has-warning'
      : '';
  const pricingIconClassName = hasPricingErrors
    ? 'icon fas fa-exclamation-triangle danger fa-lg'
    : hasPricingWarnings
      ? 'icon fas fa-exclamation-circle warning fa-lg'
      : 'icon far fa-tags fa-lg';
  const pricingTextClassName = hasPricingErrors
    ? 'item-text danger'
    : hasPricingWarnings
      ? 'item-text warning'
      : 'item-text';

  return (
    <div className="product-tabs">
      <ConfirmationModal
        showModal={showConfirmationModal}
        setShowModal={setShowConfirmationModal}
        message={confirmationModalBody}
        header={confirmationModalHeader}
        handleConfirm={confirmationModalAction}
      ></ConfirmationModal>
      {album.id && (
        <MoveTracksToNewVolumeConfirmationModal
          show={showMoveTracksToNewVolumeConfirmationModal}
          onClose={() => setShowMoveTracksToNewVolumeConfirmationModal(false)}
          usesSides={usesSides}
          volumeNumbers={volumeNumbers}
          onAddVolume={(args) => AddVolume('add', args)}
          albumId={album.id}
          selectedTracks={selectedTracks}
          allTracks={tracks}
          reloadTracks={reloadTracks}
        />
      )}
      {showAddMultipleTracks ? (
        <AddMultipleTracksModal
          onSubmit={handleAddMultipleTracks}
          showModal={showAddMultipleTracks}
          setShowModal={setShowAddMultipleTracks}
        ></AddMultipleTracksModal>
      ) : null}
      {showMoveTracksToVolumeModal && album.id ? (
        <MoveTracksToVolumeModal
          show={showMoveTracksToVolumeModal}
          onClose={() => setShowMoveTracksToVolumeModal(false)}
          usesSides={usesSides}
          volumeNumbers={volumeNumbers}
          onAddVolume={(args) => AddVolume('add', Number(args))}
          albumId={album.id}
          selectedTracks={selectedTracks}
          allTracks={tracks}
          reloadTracks={reloadTracks}
        />
      ) : null}
      {showCopyProductMetadataModal && album.id ? (
        <CopyProductMetadataToTracksModal
          show={showCopyProductMetadataModal}
          onClose={() => setShowCopyProductMetadataModal(false)}
          albumId={album.id}
          selectedTracks={selectedTracks}
          allTracks={tracks}
          reloadTracks={reloadTracks}
          productViewMode={productViewMode}
        />
      ) : null}
      {showLookupTrackModal && lookupTrack ? (
        <LookupTrackModal
          show={showLookupTrackModal}
          onClose={() => setShowLookupTrackModal(false)}
          source={lookupSource}
          track={lookupTrack}
          targetFormatName={album.format?.name ?? null}
          labelId={album.label?.adminOrgId}
          reloadTracks={reloadTracks}
        />
      ) : null}
      <Tabs
        selectedIndex={tabIndex}
        onSelect={(index) => setTabIndex(index)}
        id="controlled-tabs"
        default
      >
        <TabList className="nav nav-tabs">
          <Tab
            title={isValidFormat ? '' : 'Please Select Valid Format'}
            className={isValidFormat ? '' : 'has-error'}
          >
            <a className="nav-tabs-product">
              <i
                className={
                  isValidFormat
                    ? 'icon fas fa-music fa-lg'
                    : 'icon fas fa-exclamation-triangle danger fa-lg'
                }
              ></i>
              <span
                className={isValidFormat ? 'item-text' : 'item-text danger'}
              >
                {t('track.tracks')}
              </span>
              <span className={'padding-left-x1'}>
                <ChangeRequestCounter
                  counter={currentChanges?.tracksChanges.changesCounter}
                ></ChangeRequestCounter>
              </span>
            </a>
          </Tab>
          <Tab className={isValidRestrictions ? '' : 'has-error'}>
            <a className="nav-tabs-product">
              <i
                className={
                  isValidRestrictions
                    ? 'icon fas fa-ban fa-lg'
                    : 'icon fas fa-exclamation-triangle danger fa-lg'
                }
              ></i>
              <span
                className={
                  isValidRestrictions ? 'item-text' : 'item-text danger'
                }
              >
                {t('product.restrictions.territories')}
              </span>
              <span className={'padding-left-x1'}>
                <ChangeRequestCounter
                  counter={currentChanges?.territoriesChanges.changesCounter}
                ></ChangeRequestCounter>
              </span>
            </a>
          </Tab>
          <Tab className={pricingTabClassName}>
            <a className="nav-tabs-pricing">
              <i className={pricingIconClassName}></i>
              <span className={pricingTextClassName}>
                {t('product.pricing')}
              </span>
              <span className={'padding-left-x1'}>
                <ChangeRequestCounter
                  counter={currentChanges?.pricingChanges.changesCounter}
                ></ChangeRequestCounter>
              </span>
            </a>
          </Tab>
          {stateHistory?.length &&
          productViewMode !== ProductViewModes.ChangeRequest &&
          productViewMode !== ProductViewModes.ChangeRequestReadOnly ? (
            <Tab className="">
              <a className="nav-tabs-product">
                <i className="icon far fa-history fa-lg"></i>
                <span className="item-text">{t('product.history')}</span>
              </a>
            </Tab>
          ) : null}
          {groupedProducts?.length ? (
            <Tab className="">
              <a className="nav-tabs-product">
                <i className="icon fas fa-link fa-lg"></i>
                <span className="item-text">
                  {t('product.grouped_products')}
                </span>
              </a>
            </Tab>
          ) : null}
        </TabList>
        <div className="tab-content">
          <TabPanel>
            <TrackGrid
              showReorderTracks={showReorderTracks}
              setShowReorderTracks={setShowReorderTracks}
              tracks={augmentedTracks}
              volumeNumbers={volumeNumbers}
              updateProductTracks={updateProductTracks}
              formatHeader={formatHeader}
              readonly={readonly}
              isSelectedAllChecked={isSelectedAllChecked}
              isSelectedAllIntermediate={isSelectedAllIntermediate}
              handleSelectAllTracks={handleSelectAllTracks}
              editSelectedTracks={editSelectedTracks}
              handleCopyProductMetadataClick={handleCopyProductMetadataClick}
              handleAddToVolumeClick={handleAddToVolumeClick}
              usesSides={usesSides}
              deleteSelectedTracks={deleteSelectedTracks}
              AddVolume={AddVolume}
              addNewTrack={addNewTrack}
              addMultipleTracks={addMultipleTracks}
              gridsRef={gridsRef}
              rowSelection={rowSelection}
              onSelectionChanged={onSelectionChanged}
              getLocaleText={getLocaleText}
              finalColumnDefs={finalColumnDefs}
              addRemoveTrack={addRemoveTrack}
              toggleTrackPopUp={toggleTrackPopUp}
              getRowId={getRowId}
              productViewMode={productViewMode}
              trackChangeRequestChanges={currentChanges?.tracksChanges}
            />
          </TabPanel>
          <TabPanel>
            {restrictions ? (
              <RestrictionsTab
                readonly={readonly}
                restrictions={restrictions}
                toggleRestrictionsModal={toggleRestrictionsModal}
              ></RestrictionsTab>
            ) : (
              <span>{t('generic.loading')}</span>
            )}
          </TabPanel>
          <TabPanel>
            <PriceTab
              album={album}
              readonly={readonly}
              togglePricingModal={togglePricingModal}
              errors={errors}
              changeRequest={changeRequest}
              currentChanges={currentChanges?.pricingChanges}
            ></PriceTab>
          </TabPanel>
          {stateHistory?.length &&
          productViewMode !== ProductViewModes.ChangeRequest ? (
            <TabPanel>
              <StateHistoryTab
                stateHistory={stateHistory}
                album={album}
              ></StateHistoryTab>
            </TabPanel>
          ) : null}
          {groupedProducts?.length ? (
            <TabPanel>
              <GroupedProducts albums={groupedProducts ?? []} />
            </TabPanel>
          ) : null}
        </div>
      </Tabs>
    </div>
  );
}

export default ProductTabs;
